import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Filter, RefreshCw, X, Eye, Search, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import commonStyles from '../../styles/common.module.css';
import styles from './EstimatesPage.module.css';
import Modal from 'react-modal';
import DateRangePicker from '../Shared/DateRangePicker';
import SearchableSelect from '../Shared/SearchableSelect';
import { shouldSendToTelegram } from '../../utils/telegram';

Modal.setAppElement('#root');

const readApiError = async (error, fallback) => {
  const payload = error?.response?.data;
  if (payload instanceof Blob) {
    try {
      const parsed = JSON.parse(await payload.text());
      return parsed.error || parsed.detail || fallback;
    } catch (_parseError) {
      return fallback;
    }
  }
  return payload?.error || payload?.detail || fallback;
};

const EstimatesPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [repairRequests, setRepairRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [serviceCenters, setServiceCenters] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedServiceCenter, setSelectedServiceCenter] = useState('');
  const [requestNumbers, setRequestNumbers] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const projectOptions = useMemo(() => [
    { value: '', label: 'Выберите проект' },
    ...allProjects.map((project) => ({ value: String(project.id), label: project.name })),
  ], [allProjects]);
  const serviceCenterOptions = useMemo(() => [
    { value: '', label: 'Все сервисные центры' },
    ...serviceCenters.map((center) => ({ value: String(center.id), label: center.name })),
  ], [serviceCenters]);
  const customerOptions = useMemo(() => [
    { value: '', label: 'Выберите заказчика' },
    ...customers.map((customer) => ({
      value: String(customer.id),
      label: `${customer.name} (ИНН: ${customer.inn || '-'})`,
    })),
  ], [customers]);

  const fetchFilters = useCallback(async () => {
    try {
      setIsFiltersLoading(true);
      const params = new URLSearchParams();
      if (selectedProject) params.append('project_id', selectedProject);
      if (selectedServiceCenter) params.append('service_center_id', selectedServiceCenter);
      if (selectedCustomer) params.append('customer_id', selectedCustomer);

      setAllProjects(user?.projects || []);

      const [serviceCenterRes, customerRes] = await Promise.all([
        api.get(`/core/service-centers/?${params.toString()}`),
        api.get(`/core/customers/?${params.toString()}`),
      ]);

      setServiceCenters(Array.isArray(serviceCenterRes.data) ? serviceCenterRes.data : []);
      setCustomers(Array.isArray(customerRes.data) ? customerRes.data : []);

      console.debug('Загруженные фильтры:', {
        projects: user?.projects?.length || 0,
        serviceCenters: serviceCenterRes.data.length,
        customers: customerRes.data.length,
      });
    } catch (err) {
      console.error('Ошибка при загрузке фильтров:', err);
      toast.error('Ошибка при загрузке фильтров', { toastId: 'fetch-filters-error', autoClose: 3000 });
    } finally {
      setIsFiltersLoading(false);
    }
  }, [selectedProject, selectedServiceCenter, selectedCustomer, user]);

  const getQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedProject) params.append('project_id', selectedProject);
    if (selectedServiceCenter) params.append('service_center_id', selectedServiceCenter);
    if (selectedCustomer) params.append('customer_id', selectedCustomer);
    if (requestNumbers.trim()) params.append('search', requestNumbers.trim());
    if (startDate && endDate && startDate <= endDate) {
      params.append('start_date', startDate.toISOString().split('T')[0]);
      params.append('end_date', endDate.toISOString().split('T')[0]);
    } else if (startDate || endDate) {
      toast.warn('Выберите корректный диапазон дат', { toastId: 'invalid-dates', autoClose: 3000 });
    }
    params.append('page', currentPage);
    params.append('page_size', pageSize);
    return params;
  }, [selectedProject, selectedServiceCenter, selectedCustomer, requestNumbers, startDate, endDate, currentPage]);

  const fetchData = useCallback(async () => {
    if (!selectedProject && !selectedCustomer) {
      setRepairRequests([]);
      setTotalPages(1);
      return;
    }

    try {
      setIsLoading(true);
      const params = getQueryParams();
      const requestsRes = await api.get('/repairs/repair-requests/', { params });
      setRepairRequests(Array.isArray(requestsRes.data.results) ? requestsRes.data.results : []);
      setTotalPages(Math.ceil(requestsRes.data.count / pageSize));

      console.debug('Загруженные заявки:', requestsRes.data.results?.length || 0);
    } catch (err) {
      console.error('Ошибка при загрузке данных:', err);
      toast.error('Ошибка при загрузке данных', { toastId: 'fetch-error', autoClose: 3000 });
      setRepairRequests([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [getQueryParams]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchFilters();
    }
  }, [fetchFilters, authLoading, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const handleCustomerSelect = useCallback((value) => {
    setSelectedCustomer(value);
    setCurrentPage(1);
    console.debug('Выбран заказчик:', value);
  }, []);

  const handleProjectSelect = useCallback((value) => {
    setSelectedProject(value);
    setCurrentPage(1);
    console.debug('Выбран проект:', value);
  }, []);

  const handleServiceCenterSelect = useCallback((value) => {
    setSelectedServiceCenter(value);
    setCurrentPage(1);
    console.debug('Выбран сервисный центр:', value);
  }, []);

  const handleRequestNumbersChange = useCallback((e) => {
    setRequestNumbers(e.target.value);
    setCurrentPage(1);
    console.debug('Введены номера заявок:', e.target.value);
  }, []);

  const handleRangeChange = useCallback((from, to) => {
    setStartDate(from);
    setEndDate(to);
    setCurrentPage(1);
  }, []);

  const toggleFilters = () => {
    setIsFiltersOpen(prev => !prev);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedProject('');
    setSelectedServiceCenter('');
    setSelectedCustomer('');
    setRequestNumbers('');
    setStartDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    setEndDate(new Date());
    setCurrentPage(1);
    console.debug('Фильтры очищены');
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const findEstimateTemplate = async (customerId, projectId) => {
    try {
      const response = await api.get('/reports/templates/', {
        params: {
          customer_id: customerId || undefined,
          project_id: projectId,
          target_page: 'estimates',
        },
      });
      const templates = response.data;
      let template = templates.find(t => t.customer?.id === parseInt(customerId) && t.project.id === parseInt(projectId));
      if (!template) {
        template = templates.find(t => !t.customer && t.project.id === parseInt(projectId));
      }
      return template;
    } catch (err) {
      console.error('Ошибка при поиске шаблона сметы:', err);
      return null;
    }
  };

  const fetchEstimatePreview = async (repairRequestId, template) => {
    const response = await api.get('/reports/estimate-preview/', {
      params: {
        repair_request_id: repairRequestId,
        template_id: template?.id || undefined,
      },
    });
    const data = response.data;
    return {
      ...data,
      items: (data.items || []).map(item => ({
        ...item,
        total_cost:
          (item.repair_codes || []).reduce((sum, code) => sum + (parseFloat(code.price) || 0), 0) +
          (item.diagnostic_codes || []).reduce((sum, code) => sum + (parseFloat(code.price) || 0), 0),
      })),
    };
  };

  const handleDownloadEstimate = async (repairRequestId) => {
    try {
      const repairRequest = repairRequests.find(req => req.id === parseInt(repairRequestId));
      if (!repairRequest) {
        throw new Error('Заявка не найдена в списке');
      }

      const customerId = repairRequest.customer?.id;
      const projectId = repairRequest.project?.id;

      const template = await findEstimateTemplate(customerId, projectId);
      const readiness = await fetchEstimatePreview(repairRequestId, template);

      if (!readiness.eligible_count) {
        setPreviewData(readiness);
        setIsPreviewOpen(true);
        toast.info(readiness.message, {
          toastId: `estimate-not-ready-${repairRequestId}`,
          autoClose: 8000,
        });
        return;
      }

      if (template) {
        console.debug('Используется шаблон сметы:', template.id, template.name);
        const params = {
          template_id: template.id,
          repair_request_id: repairRequestId,
        };

        if (shouldSendToTelegram()) {
          try {
            await api.get('/reports/generate/', { params: { ...params, send_to_telegram: true } });
            toast.success('Смета отправлена в Telegram', { autoClose: 3000 });
            return;
          } catch (err) {
            console.warn('Telegram send failed, fallback to download:', err);
          }
        }

        const response = await api.get('/reports/generate/', {
          params,
          responseType: 'blob',
        });

        const filename = `${template.name}_${repairRequest.customer?.name || 'UnknownCustomer'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Смета по шаблону успешно выгружена', { autoClose: 3000 });
      } else {
        console.debug('Шаблон не найден, используется стандартная смета');
        const payload = { repair_request_id: repairRequestId, scope: 'standard' };
        if (shouldSendToTelegram()) {
          try {
            await api.post('/reports/generate_approval_estimates/', { ...payload, send_to_telegram: true });
            toast.success('Смета отправлена в Telegram', { autoClose: 3000 });
            return;
          } catch (err) {
            console.warn('Telegram send failed, fallback to download:', err);
          }
        }

        const response = await api.post('/reports/generate_approval_estimates/', payload, { responseType: 'blob' });

        const filename = `estimate_${repairRequestId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Стандартная смета успешно выгружена', { autoClose: 3000 });
      }
    } catch (err) {
      console.error('Ошибка при скачивании сметы:', err);
      const message = await readApiError(err, 'Не удалось скачать смету. Повторите попытку.');
      toast.error(message, { toastId: 'download-error', autoClose: 8000 });
    }
  };

  const handlePreviewEstimate = async (repairRequestId) => {
    try {
      setPreviewLoading(true);
      setIsPreviewOpen(true);
      const repairRequest = repairRequests.find(req => req.id === parseInt(repairRequestId));
      if (!repairRequest) throw new Error('Заявка не найдена в списке');
      const template = await findEstimateTemplate(
        repairRequest.customer?.id,
        repairRequest.project?.id
      );
      const preview = await fetchEstimatePreview(repairRequestId, template);
      setPreviewData(preview);
      console.debug('Предпросмотр сметы:', { repairRequestId, items: preview.items.length });
    } catch (err) {
      console.error('Ошибка при загрузке предпросмотра:', err);
      const message = await readApiError(err, 'Не удалось загрузить предпросмотр сметы.');
      toast.error(message, { toastId: 'preview-error', autoClose: 8000 });
      setIsPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredRequests = useMemo(() => {
    let filtered = repairRequests;

    if (selectedProject) {
      filtered = filtered.filter(req => req.project?.id === parseInt(selectedProject));
    }

    if (selectedServiceCenter) {
      filtered = filtered.filter(req => req.service_center?.id === parseInt(selectedServiceCenter));
    }

    if (selectedCustomer) {
      filtered = filtered.filter(req => req.customer?.id === parseInt(selectedCustomer));
    }

    if (requestNumbers.trim()) {
      const numbers = requestNumbers
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(num => num);
      filtered = filtered.filter(req =>
        numbers.some(num => req.request_number.toLowerCase().includes(num))
      );
    }

    console.debug('Отфильтрованные заявки:', filtered.length);
    return filtered;
  }, [repairRequests, selectedProject, selectedServiceCenter, selectedCustomer, requestNumbers]);

  const headerActions = (
    <div className={styles.headerActions}>
      <button
        className={`${styles.actionButton} ${styles.filterButton} ${isFiltersOpen ? styles.active : ''}`}
        onClick={toggleFilters}
      >
        <Filter size={18} />
        <span>Фильтры</span>
        <div className={`${styles.chevron} ${isFiltersOpen ? styles.open : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.427 9.573L8 6l3.573 3.573a.5.5 0 0 0 .854-.353V4.5A.5.5 0 0 0 11.927 4H4.073a.5.5 0 0 0-.5.5v4.72a.5.5 0 0 0 .854.353z"/>
          </svg>
        </div>
      </button>

      <button className={`${styles.actionButton} ${styles.refreshButton}`} onClick={handleRefresh}>
        <RefreshCw size={18} />
        <span>Обновить</span>
      </button>
    </div>
  );

  if (authLoading) {
    return (
      <div className={`${commonStyles.pageContainer} ${styles.loadingContainer}`}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? styles.embeddedPage : commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      {embedded ? (
        <div className={styles.embeddedToolbar}>{headerActions}</div>
      ) : (
        <div className={styles.pageHeader}>
          <button className={styles.backButton} onClick={() => navigate('/select-project')}>
            <ArrowLeft size={20} />
            <span>Назад к выбору проекта</span>
          </button>

          <div className={styles.headerContent}>
            <div className={styles.titleSection}>
              <div className={styles.titleGroup}>
                <div className={styles.iconWrapper}>
                  <FileText className={styles.titleIcon} />
                </div>
                <div>
                  <h1 className={styles.pageTitle}>Управление сметами</h1>
                  <p className={styles.pageSubtitle}>Просмотр и загрузка смет по заявкам на ремонт</p>
                </div>
              </div>
            </div>

            {headerActions}
          </div>
        </div>
      )}

      <div className={`${styles.filtersContainer} ${isFiltersOpen ? styles.visible : ''}`}>
        {isFiltersLoading ? (
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Загрузка фильтров...</p>
          </div>
        ) : (
          <div className={styles.filtersContent}>
            <div className={styles.filtersGrid}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <Calendar size={16} />
                  <span>Период</span>
                </label>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleRangeChange}
                  className={styles.datePickerWrapper}
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <span>Проект</span>
                </label>
                <SearchableSelect
                  options={projectOptions}
                  value={selectedProject}
                  onChange={handleProjectSelect}
                  placeholder="Выберите проект"
                  ariaLabel="Проект"
                  isLoading={isFiltersLoading}
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <span>Сервисный центр</span>
                </label>
                <SearchableSelect
                  options={serviceCenterOptions}
                  value={selectedServiceCenter}
                  onChange={handleServiceCenterSelect}
                  placeholder="Все сервисные центры"
                  ariaLabel="Сервисный центр"
                  isLoading={isFiltersLoading}
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <span>Заказчик</span>
                </label>
                <SearchableSelect
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={handleCustomerSelect}
                  placeholder="Выберите заказчика"
                  ariaLabel="Заказчик"
                  isLoading={isFiltersLoading}
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <span>Поиск по номеру</span>
                </label>
                <div className={styles.searchInputWrapper}>
                  <Search size={16} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Введите номера заявок..."
                    value={requestNumbers}
                    onChange={handleRequestNumbersChange}
                    className={styles.searchInput}
                  />
                </div>
              </div>

              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                Очистить фильтры
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableHeaderContent}>
            <h3 className={styles.sectionTitle}>
              Заявки на ремонт
              <span className={styles.countBadge}>{filteredRequests.length}</span>
            </h3>
          </div>
        </div>
        
        <div className={styles.tableWrapper}>
          {(!selectedProject && !selectedCustomer) ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <FileText size={48} />
              </div>
              <h3>Выберите проект или заказчика</h3>
              <p>Для отображения заявок выберите проект или заказчика в фильтрах.</p>
            </div>
          ) : (isLoading || isFiltersLoading) ? (
            <div className={styles.tableLoading}>
              <div className={styles.tableSpinner}></div>
              <p>{isLoading ? 'Загрузка данных...' : 'Применяю фильтры...'}</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <FileText size={48} />
              </div>
              <h3>Заявки не найдены</h3>
              <p>Попробуйте изменить параметры фильтрации или очистить фильтры</p>
            </div>
          ) : (
            <>
              <div className={styles.tableScrollWrapper}>
                <table className={styles.requestsTable}>
                  <thead>
                    <tr>
                      <th>Номер заявки</th>
                      <th>Заказчик</th>
                      <th>ИНН</th>
                      <th>Проект</th>
                      <th>Сервисный центр</th>
                      <th>Всего в заявке</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map(req => (
                      <tr key={req.id} className={styles.requestRow}>
                        <td>
                          <span className={styles.requestNumber}>{req.request_number}</span>
                        </td>
                        <td>
                          <span className={styles.customerName}>{req.customer?.name || '-'}</span>
                        </td>
                        <td>
                          <span className={styles.inn}>{req.customer?.inn || '-'}</span>
                        </td>
                        <td>
                          <span className={styles.projectName}>{req.project?.name || '-'}</span>
                        </td>
                        <td>
                          <span className={styles.serviceCenterName}>{req.service_center?.name || '-'}</span>
                        </td>
                        <td>
                          <span className={styles.equipmentCount}>{req.total_equipment || 0}</span>
                        </td>
                        <td>
                          <div className={styles.actionsCell}>
                            <button
                              className={`${styles.actionButton} ${styles.previewButton}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewEstimate(req.id);
                              }}
                              title="Предпросмотр сметы"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              className={`${styles.actionButton} ${styles.downloadButton}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadEstimate(req.id);
                              }}
                              title="Скачать смету"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={styles.pagination}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className={styles.paginationButton}
                >
                  Назад
                </button>
                <span className={styles.paginationInfo}>
                  Страница {currentPage} из {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={styles.paginationButton}
                >
                  Вперед
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={isPreviewOpen}
        onRequestClose={() => setIsPreviewOpen(false)}
        className={styles.previewModal}
        overlayClassName={styles.previewOverlay}
      >
        <div className={styles.previewContent}>
          <div className={styles.previewHeader}>
            <div className={styles.previewTitle}>
              <FileText size={20} />
              <span>Предпросмотр сметы</span>
            </div>
            <button 
              className={styles.closeButton} 
              onClick={() => setIsPreviewOpen(false)}
            >
              <X size={18} />
            </button>
          </div>
          
          {previewLoading ? (
            <div className={styles.previewLoading}>
              <div className={styles.spinner}></div>
              <p>Загрузка предпросмотра...</p>
            </div>
          ) : previewData ? (
            <div className={styles.previewBody}>
              <div className={styles.previewInfo}>
                <div className={styles.infoCard}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Заказчик:</span>
                    <span className={styles.infoValue}>{previewData.customer?.name || '-'}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>ИНН:</span>
                    <span className={styles.infoValue}>{previewData.customer?.inn || '-'}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.previewTableContainer}>
                {previewData.items.length === 0 ? (
                  <div className={styles.emptyPreview}>
                    <FileText size={32} />
                    <h3>Смета пока недоступна</h3>
                    <p>{previewData.message || 'В заявке пока нет оборудования, готового для включения в смету.'}</p>
                    {previewData.stage_summary?.length > 0 && (
                      <div className={styles.stageSummary}>
                        <span className={styles.stageSummaryTitle}>Текущие этапы оборудования</span>
                        <div className={styles.stageSummaryList}>
                          {previewData.stage_summary.map(stage => (
                            <span key={stage.status} className={styles.stageSummaryItem}>
                              {stage.label}
                              <strong>{stage.count}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={styles.previewTableWrapper}>
                    <table className={styles.previewTable}>
                      <thead>
                        <tr>
                          <th>№ п/п</th>
                          <th>Серийный номер</th>
                          <th>Модель</th>
                          <th>Работа</th>
                          <th>Стоимость</th>
                          <th>Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.items.map((item, idx) => {
                          const allCodes = [
                            ...(item.repair_codes || []).map(rc => ({ ...rc, type: 'repair' })),
                            ...(item.diagnostic_codes || []).map(dc => ({ ...dc, type: 'diagnostic' })),
                          ];
                          return allCodes.length > 0 ? allCodes.map((code, codeIdx) => (
                            <tr key={`${item.serial_number}-${codeIdx}`}>
                              {codeIdx === 0 && (
                                <>
                                  <td rowSpan={allCodes.length} className={styles.indexCell}>
                                    {idx + 1}
                                  </td>
                                  <td rowSpan={allCodes.length} className={styles.serialCell}>
                                    {item.serial_number}
                                  </td>
                                  <td rowSpan={allCodes.length} className={styles.modelCell}>
                                    {item.equipment_model}
                                  </td>
                                </>
                              )}
                              <td className={styles.workCell}>
                                <span className={styles.workCode}>[{code.code}]</span>
                                <span className={styles.workName}>{code.name}</span>
                                <span className={styles.workType}>
                                  ({code.type === 'repair' ? 'Ремонт' : 'Диагностика'})
                                </span>
                              </td>
                              <td className={styles.priceCell}>
                                {parseFloat(code.price || 0).toLocaleString()} ₽
                              </td>
                              {codeIdx === 0 && (
                                <td rowSpan={allCodes.length} className={styles.statusCell}>
                                  {item.repair_status_display || item.repair_status}
                                </td>
                              )}
                            </tr>
                          )).concat(
                            <tr key={`${item.serial_number}-total`} className={styles.subtotalRow}>
                              <td colSpan={4} className={styles.subtotalLabel}>Промежуточный итог:</td>
                              <td className={styles.subtotalValue}>
                                {item.total_cost.toLocaleString()} ₽
                              </td>
                              <td></td>
                            </tr>
                          ) : (
                            <tr key={`${item.serial_number}-no-codes`}>
                              <td className={styles.indexCell}>{idx + 1}</td>
                              <td className={styles.serialCell}>{item.serial_number}</td>
                              <td className={styles.modelCell}>{item.equipment_model}</td>
                              <td className={styles.workCell}>-</td>
                              <td className={styles.priceCell}>0 ₽</td>
                              <td className={styles.statusCell}>
                                {item.repair_status_display || item.repair_status}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className={styles.totalRow}>
                          <td colSpan={4} className={styles.totalLabel}>
                            <strong>Общая стоимость:</strong>
                          </td>
                          <td className={styles.totalValue}>
                            <strong>
                              {previewData.items.reduce((sum, item) => sum + item.total_cost, 0).toLocaleString()} ₽
                            </strong>
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.emptyPreview}>
              <FileText size={32} />
              <p>Нет данных для предпросмотра</p>
            </div>
          )}
          
          {previewData && previewData.items.length > 0 && (
            <div className={styles.previewFooter}>
              <button
                className={styles.downloadEstimateButton}
                onClick={() => handleDownloadEstimate(previewData.repair_request_id)}
              >
                <Download size={16} />
                Скачать Excel
              </button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default EstimatesPage;
